import {
  Box,
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { isResponseWithData } from 'lib-shared-auth';
import { SubmitHandler, useForm } from 'react-hook-form';

import { solvemateChatbotsApi } from 'apis/chatbots';
import { HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { SettingsIntegrationsDocumentationButton } from '../../../../components/documentationButton/documentationButton';
import {
  defaultChatbotName,
  pathToChatbotsSettings,
} from '../../chatbots.view';

import styles from './chatbotsCreate.view.module.scss';

type ChatbotCreateFormValues = {
  url: string;
};

/** Solvemate Chatbots but perhaps this can be extended in the future. */
export const ChatbotsCreateView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const notify = useNotifications();

  const [createChatbot] =
    solvemateChatbotsApi.useCreateSolvemateChatbotMutation();

  const { control, handleSubmit } = useForm<ChatbotCreateFormValues>({
    mode: 'all',
    defaultValues: {
      url: '',
    },
  });

  const onSubmit: SubmitHandler<ChatbotCreateFormValues> = async ({ url }) => {
    const response = await createChatbot({
      url,
      // Currently we only support Solvemate as 'type'
      _type: 'solvemate-from-url',
    }).unwrap();

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Success',
          text: `"${
            response.data?.name || defaultChatbotName
          }" has been created.`,
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      navigate({ path: pathToChatbotsSettings });
    } else {
      notify(
        {
          title: 'Error while creating the chatbot.',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <>
      <Page.Section
        title="Create chatbot"
        titleMeta={
          <SettingsIntegrationsDocumentationButton
            onClick={() =>
              elevioClient.openArticle(articles.settings.chatbots.documentation)
            }
          />
        }
        controls={
          <Button onClick={handleSubmit(onSubmit)}>Save changes</Button>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <FormField span={4}>
              <HookFormText
                name="url"
                label="Solvemate URL"
                control={control}
                componentProps={{
                  placeholder: 'Insert your Solvemate URL',
                }}
                rules={{
                  required: 'This field is required',
                  pattern: {
                    // Checks if the string contains '/chatbot/123456abc'
                    value: /.*\/chatbot\/([0-9a-z]+).*/,
                    message: 'This does not look like a Solvemate URL',
                  },
                }}
              />
            </FormField>
          </FormGrid>
        </form>
      </Page.Section>

      <Page.Section>
        <FlexBox flexDirection="column" gap="300">
          <Text.Paragraph>
            To integrate the chatbot, your Solvemate&apos;s chatbot URL is
            needed!
          </Text.Paragraph>

          <Text.Paragraph>
            You can find it by going to your Solvemate chatbot dashboard and
            copy the URL from the browser address bar and paste it in the input
            below.
          </Text.Paragraph>

          <FlexBox
            display="inline-flex"
            flexDirection="row"
            gap="250"
            margin="300 0"
            className={styles.solvemateExampleLink}
          >
            <Icon name="lock" size={20} />

            <Box className={styles.solvemateExampleLinkLabel}>
              https://app.solvemate.com/org/1234567890/chatbot/1234567890/dashboard
            </Box>
          </FlexBox>
        </FlexBox>
      </Page.Section>
    </>
  );
};
